import { useEffect, useReducer } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProject } from '@/lib/offline/idb/projects';
import { initialQuoteState, type QuoteState } from './types';
import { quoteReducer } from './quoteReducer';
import { QuotePageView } from './QuotePageView';
import { QuoteHydrationSkeleton } from './QuoteHydrationSkeleton';
import { seedQuoteFromAnketa } from './state/seedFromAnketa';
import { useQuoteDraftStore } from './state/useQuoteDraftStore';
import {
  loadQuoteDraftFromIdb,
  saveQuoteDraftToIdb,
  clearQuoteDraftFromIdb,
} from './state/quoteDraftIdbSync';
import { loadLatestSavedQuoteByDraftKey, loadSavedQuote } from './state/savedQuotesIdb';
import { useForwardToContract } from './useForwardToContract';

/**
 * Block 12 PR-D-A Phase D5 — Quote page container (ADR-0005).
 *
 * **Hydration cascade (Step 9, mirror hotfix-2C C5 Step 12):**
 *   0.   PR-H5a ADR-0031 — `?new=true && !projectId` → blank (skip всё).
 *   0.5. PR-H5b ADR-0032 — `?savedQuoteId=<uuid>` → `loadSavedQuote(id)` →
 *      explicit restore конкретного сохранённого КП (persistent intent,
 *      bookmark-able). После Priority 0, перед Zustand. Не найден → fall through.
 *   1. Zustand `useQuoteDraftStore.getDraft(draftKey)` — sync fast cache
 *      (cross-route SPA nav в той же сессии).
 *   2. IDB `loadQuoteDraftFromIdb(draftKey)` — durable cross-session draft
 *      (F5 / browser restart / cross-tab last-writer-wins).
 *   2.5. PR-H1 #2 fix — `loadLatestSavedQuoteByDraftKey(draftKey)` — после
 *      handleSave (BottomActionButtons) draft IDB очищается, но savedQuote
 *      остаётся в `saved-quotes` IDB. Cascade читает savedQuote как
 *      canonical post-save state (browser back из Договора → Quote показывает
 *      только что сохранённый snapshot, а не blank/seed).
 *   2.6. PR-H2 ADR-0027 — invalidation check после Priority 1/2/2.5 hit:
 *      если есть projectId AND hydrated state.lastAnketaUpdatedAt < current
 *      project.updatedAt, dispatch PARTIAL_RESEED для refresh anketa-derived
 *      полей (composition / cost / furnitureType — PR-H7: furniture-name
 *      contact больше не существует per ADR-0030 §8), preserve user-owned
 *      (conditions / comments / contacts / custom UUID composition rows).
 *      Resolves PR-H1 known tradeoff B-167.
 *   3. Если есть projectId → `seedQuoteFromAnketa(projectId)` (Step 8).
 *   4. Иначе blank ready.
 *
 * `draftKey` = projectId если из анкеты, иначе литерал 'standalone'.
 *
 * **Autosave (Step 9c, mirror C5 Step 12):** 300ms true-debounce —
 *   - после каждого `state` изменения сбрасываем pending timer
 *   - save срабатывает после `delayMs` тишины → setDraft Zustand + saveQuoteDraftToIdb
 *   - unmount cleanup отменяет pending timer (lost ≤300ms input acceptable)
 *   - guard: не пишем если hydrationStatus !== 'ready' (предотвращает запись
 *     blank-initial state на mount до hydration)
 *
 * **clearDraft** срабатывает на Save в Step 11 (BottomActionButtons handler).
 *
 * URL: `/calculator/quote?projectId=<uuid>` (wired в AnketaFormContainer
 * onCreateQuote — ADR-0020 §Q11).
 */
const AUTOSAVE_DEBOUNCE_MS = 300;

export function QuotePageContainer() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const draftKey = projectId ?? 'standalone';

  const [state, dispatch] = useReducer(quoteReducer, initialQuoteState);

  // PR-C1 (ADR-0035) Phase 1 — forward-chain «Вперёд» КП → существующий Договор.
  // Async-проверка наличия downstream-договора (контейнер-слой, ADR-0005) →
  // готовый nav-path либо null (кнопка скрыта). Чистое чтение IDB (R2).
  const forwardContractPath = useForwardToContract(draftKey, searchParams.get('savedQuoteId'));

  // Hydration cascade. Mirror Phase C5 AnketaFormContainer pattern:
  // `cancelled` flag per-mount handles React StrictMode double-invoke.
  // First hydrate self-cancels через cleanup; second hydrate completes
  // fresh со своим cancelled=false. Idempotent HYDRATE → double-dispatch
  // race в Priority 1 (sync Zustand hit) harmless: same payload, same state.
  //
  // **Не использовать `useRef` guard** — preventing второго mount'а run'а
  // оставляет hydration stuck при self-cancellation первого (StrictMode
  // dev deadlock наблюдался в Phase D9 visual review).
  useEffect(() => {
    let cancelled = false;

    // PR-H2 ADR-0027 — Priority 2.6 invalidation check. Запускается после
    // Priority 1/2/2.5 hit (Priority 3 anketa seed только что заполнил
    // timestamps — invalidation там не нужен). Lazy pull model: при mount
    // КП проверяет `project.updatedAt > state.lastAnketaUpdatedAt` и dispatch'ит
    // PARTIAL_RESEED для refresh anketa-derived полей.
    //
    // Backward-compat: legacy drafts без `lastAnketaUpdatedAt` (undefined) → skip
    // (treat as «assume fresh»), existing UX preserved.
    async function checkAndReseed(hydratedState: Partial<QuoteState>): Promise<void> {
      if (!projectId || hydratedState.lastAnketaUpdatedAt === undefined) return;
      try {
        const currentProject = await getProject(projectId);
        if (cancelled || !currentProject) return;
        if (currentProject.updatedAt > hydratedState.lastAnketaUpdatedAt) {
          const reseedPayload = await seedQuoteFromAnketa(projectId);
          if (cancelled || !reseedPayload) return;
          dispatch({ type: 'PARTIAL_RESEED', payload: reseedPayload });
        }
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[QuotePageContainer] anketa invalidation check failed', err);
        }
      }
    }

    async function hydrate() {
      // ── Priority 0 — explicit «new» flag (PR-H5a, ADR-0031) ──
      // `?new=true` (без projectId) → чистый лист: пропустить ВСЕ restoration
      // paths (Zustand, IDB, savedQuote Priority 2.5, anketa re-seed/seed).
      // savedQuotes IDB НЕ трогаем — постоянные сохранения пользователя.
      const isNewQuote = searchParams.get('new') === 'true';
      if (isNewQuote && !projectId) {
        useQuoteDraftStore.getState().clearDraft(draftKey);
        try {
          await clearQuoteDraftFromIdb(draftKey);
        } catch {
          // graceful: idb clear failure не блокирует blank state
        }
        if (!cancelled) dispatch({ type: 'SET_HYDRATION_STATUS', status: 'ready' });
        return;
      }

      // ── Priority 0.5 — explicit savedQuote restore by ID (PR-H5b, ADR-0032) ──
      // `?savedQuoteId=<uuid>` — Persistent intent (bookmark-able, URL НЕ
      // очищается, в отличие от transient `?new=true`). Кликом по элементу
      // SavedQuotesList пользователь явно выбрал конкретное сохранённое КП —
      // сильнейшее намерение после `?new=true`. Поэтому Priority 0.5: после
      // Priority 0 (blank), но перед Zustand/IDB/savedQuote fallback/seed.
      //
      // Conflict: `?savedQuoteId=` И `?new=true` вместе → Priority 0 выше уже
      // сделал return (blank wins), сюда не доходим (см. ADR-0032 conflict matrix).
      //
      // Detached session: restore идёт в текущий draftKey ('standalone' если нет
      // projectId). Original anketa link НЕ reactivated — savedQuote это snapshot,
      // не live link (ADR-0032 trade-offs). checkAndReseed НЕ вызываем: snapshot
      // не должен перезаписываться anketa-derived полями.
      //
      // Defensive: savedQuoteId не найден (stale URL после deletion) → fall
      // through к Priority 1/2/2.5/3/4 (не crash).
      const savedQuoteId = searchParams.get('savedQuoteId');
      if (savedQuoteId) {
        try {
          const saved = await loadSavedQuote(savedQuoteId);
          if (cancelled) return;
          if (saved) {
            useQuoteDraftStore.getState().setDraft(draftKey, saved.state);
            dispatch({ type: 'HYDRATE', payload: saved.state });
            return;
          }
        } catch {
          // graceful: idb недоступен → fall through к остальному cascade.
        }
      }

      // Priority 1 — Zustand cache (sync, fast).
      const fromStore = useQuoteDraftStore.getState().getDraft(draftKey);
      if (fromStore) {
        if (!cancelled) dispatch({ type: 'HYDRATE', payload: fromStore });
        if (!cancelled) await checkAndReseed(fromStore);
        return;
      }

      // Priority 2 — durable IDB mirror.
      try {
        const fromIdb = await loadQuoteDraftFromIdb(draftKey);
        if (cancelled) return;
        if (fromIdb) {
          useQuoteDraftStore.getState().setDraft(draftKey, fromIdb);
          dispatch({ type: 'HYDRATE', payload: fromIdb });
          await checkAndReseed(fromIdb);
          return;
        }
      } catch {
        // graceful: idb может не быть доступен, fall through к seed/blank
      }

      // Priority 2.5 — saved quote fallback (PR-H1 #2 fix).
      // BottomActionButtons.handleSave намеренно clears draft Zustand + IDB
      // после persist в saved-quotes (mirror C5 pattern). Без этой priority
      // browser back из Договора в Quote → cascade fall-through к seed/blank,
      // user "теряет" только что сохранённый snapshot. Здесь читаем savedQuote
      // как canonical post-save state. Same dispatch pattern что Priority 2 —
      // hydrate state + populate Zustand для следующих SPA navigation hits.
      //
      // **PR-H1 known tradeoff B-167 resolved PR-H2 ADR-0027:** anketa edits
      // после save больше не теряются — Priority 2.6 `checkAndReseed` ниже
      // dispatch'ит PARTIAL_RESEED при `project.updatedAt > state.lastAnketaUpdatedAt`.
      try {
        const savedQuote = await loadLatestSavedQuoteByDraftKey(draftKey);
        if (cancelled) return;
        if (savedQuote) {
          useQuoteDraftStore.getState().setDraft(draftKey, savedQuote.state);
          dispatch({ type: 'HYDRATE', payload: savedQuote.state });
          await checkAndReseed(savedQuote.state);
          return;
        }
      } catch {
        // graceful: fall through к seed/blank
      }

      // Priority 3 — anketa seed (Step 8) если projectId есть. Returned payload
      // имеет fresh `seededAt` + `lastAnketaUpdatedAt`, поэтому Priority 2.6
      // invalidation сюда не нужен.
      if (projectId) {
        const seed = await seedQuoteFromAnketa(projectId).catch(() => null);
        if (cancelled) return;
        if (seed) {
          dispatch({ type: 'HYDRATE', payload: seed });
          return;
        }
      }

      // Priority 4 — blank ready.
      if (!cancelled) dispatch({ type: 'SET_HYDRATION_STATUS', status: 'ready' });
    }
    hydrate();

    return () => {
      cancelled = true;
    };
  }, [projectId, draftKey, searchParams]);

  // ── R3 (ADR-0031) — `?new=true` это transient signal. После consume убираем
  // из URL через history.replaceState, иначе browser «Назад» вернёт URL с
  // `?new=true` → blank поверх заполненных полей (паттерн PR-H3 Finding 1).
  useEffect(() => {
    if (searchParams.get('new') !== 'true') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('new');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, [searchParams]);

  // Debounced autosave: after 300ms тишины — setDraft(Zustand) + saveToIdb.
  // Guard: skip пока hydration не завершилось — иначе записали бы pending
  // blank state поверх durable IDB draft (timing race).
  useEffect(() => {
    if (state.hydrationStatus !== 'ready') return;
    const timer = setTimeout(() => {
      persistDraft(draftKey, state);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [state, draftKey]);

  if (state.hydrationStatus === 'pending') {
    return <QuoteHydrationSkeleton />;
  }

  return (
    <QuotePageView
      state={state}
      dispatch={dispatch}
      projectId={projectId}
      draftKey={draftKey}
      forwardContractPath={forwardContractPath}
    />
  );
}

function persistDraft(key: string, state: QuoteState): void {
  useQuoteDraftStore.getState().setDraft(key, state);
  saveQuoteDraftToIdb(key, state).catch(() => {
    // graceful — saveQuoteDraftToIdb уже logWarn внутри; ничего не делаем
  });
}
